import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { DraggableCore } from 'react-draggable'
import { Manager, Reference } from 'react-popper'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { setBlockLayout, cloneBlocks } from '../../../store/actions/blocks'
import {
  selectBlocks,
  setDragOffset
} from '../../../store/actions/selectedBlocks'
import {
  colWidthSelector,
  dragSnappedSelector,
  sectionTopsSelector,
  rawDragOffsetSelector,
  cropBlockIdSelector
} from '../../../store/selectors/boardSelectors'
import {
  placementBlockSelector,
  editModeSelector
} from '../../../store/selectors/selectors'
import BlockContainer from './BlockContainer'
import BlockOptions from './BlockOptions/BlockOptions'
import ResizeHandle from './ResizeHandle'
import styled from 'react-emotion'

export const Selectable = styled(
  React.forwardRef(
    ({ layout, selected, dragging, interactive, ...props }, ref) => (
      <div
        {...props}
        ref={ref}
        style={{
          touchAction: 'initial' // override react-dragable hack that breaks android-chrome (https://github.com/mzabriskie/react-draggable/issues/51#issuecomment-103488278)
        }}
      />
    )
  )
)(({ layout, selected, dragging, interactive }) => {
  const selectedStyle = interactive
    ? {
        ':after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          outline: '1px solid #808080',
          // mixBlendMode: 'difference',  // Firefox-Windows hates this (blocks go invisible when you hover on them)
          pointerEvents: 'none'
        },
        cursor: dragging ? 'grabbing' : 'grab',
        [ResizeHandle]: { display: 'block' }
      }
    : {}
  const style = {
    label: 'Selectable',
    position: 'absolute',
    touchAction: 'initial', // override react-dragable hack that breaks android-chrome (https://github.com/mzabriskie/react-draggable/issues/51#issuecomment-103488278)
    ...layout.style,
    ['& > ' + ResizeHandle]: { display: 'none' },
    ':hover': selectedStyle,
    ...(selected ? selectedStyle : {})
  }
  return style
})

const enhance = compose(
  connect(
    (state, { group: { blocks } }) => {
      const id = blocks[0].group || blocks[0].id
      return {
        editMode: editModeSelector(state),
        zoom: state.board.zoom,
        textEditMode: state.board.textEditMode === id,
        selected: state.selectedBlocks.selected.includes(id),
        placing: !!placementBlockSelector(state),
        cropping: !!cropBlockIdSelector(state),
        colWidth: colWidthSelector(state),
        drag: dragSnappedSelector(state),
        dragOffset: rawDragOffsetSelector(state),
        sectionTops: sectionTopsSelector(state)
      }
    },
    (dispatch, { group: { id } }) => ({
      setBlockLayout: blocks => dispatch(setBlockLayout(blocks)),
      cloneBlocks: blocks => dispatch(cloneBlocks(blocks)),
      select: multi => dispatch(selectBlocks([id], multi)),
      setDragOffset: delta => dispatch(setDragOffset(delta))
    })
  )
)
export class SelectableContainer extends Component {
  constructor() {
    super()
    this.trackModifierKeys = this.trackModifierKeys.bind(this)
  }

  state = {}

  trackModifierKeys(event) {
    const { dragOffset, setDragOffset } = this.props
    if (!dragOffset && !event.shiftKey && !event.altKey) setDragOffset(null)

    setDragOffset({
      ...(dragOffset || { x: 0, y: 0 }),
      shiftKey: event.shiftKey,
      altKey: event.altKey
    })
  }

  dragStart(event) {
    let target = event.target
    // if target is something in the SVG, use the SVG instead - it has the 'data-action attr
    if (target.ownerSVGElement) target = target.ownerSVGElement

    // Select if not already
    if (!this.props.selected) this.props.select(event.shiftKey)
    // when a drag happens we need to know if it's on the resize handles or the block
    // it may start on the handles but move out because they are small
    // so we record where the drag started, including which handle it was
    this.setState({ action: target.getAttribute('data-action') || 'move' })

    // keep track of shift and alt keys even when a drag even doesn't happen
    document.addEventListener('keydown', this.trackModifierKeys)
    document.addEventListener('keyup', this.trackModifierKeys)
  }
  drag(event, data) {
    // don't bother if no movement
    if (data.deltaX === 0 && data.deltaY === 0) return

    const { dragOffset = { x: 0, y: 0 }, setDragOffset } = this.props

    setDragOffset({
      x: dragOffset.x + data.deltaX,
      y: dragOffset.y + data.deltaY,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      action: this.state.action
    })
  }

  dragStop(event) {
    document.removeEventListener('keydown', this.trackModifierKeys)
    document.removeEventListener('keyup', this.trackModifierKeys)

    const {
      dragOffset,
      drag,
      group,
      sectionTops,
      setBlockLayout,
      cloneBlocks
    } = this.props

    const action = this.state.action
    this.setState({ action: undefined })

    // check if this was just a click (no dragging happened)
    if (drag && dragOffset && (dragOffset.x !== 0 || dragOffset.y !== 0)) {
      const newPositions = group.blocks.map(block => {
        const offset = group.layout.getDelta(block.layout)
        const snappedLayout = drag.layout.withDelta(offset)

        // find section
        // go through sections in reverse order until the first
        // one that's not below this block
        const [sectionId, sectionTop] = [...sectionTops.entries()]
          .reverse()
          .find(([id, top]) => top <= snappedLayout.top)

        let layout = {
          column: snappedLayout.left,
          top: snappedLayout.top - sectionTop,
          section: sectionId
        }

        if (action.match(/^resize-/)) {
          layout.width = snappedLayout.width
          if (!block.aspect) {
            layout.height = snappedLayout.height
          }
        }

        if (action.match(/^crop-frame-/)) {
          layout.width = snappedLayout.width
          layout.aspect = snappedLayout.height / snappedLayout.width
        }

        return { block, layout }
      })

      if (action === 'move' && event.altKey) cloneBlocks(newPositions)
      else setBlockLayout(newPositions)
    }

    this.props.setDragOffset(undefined)
  }

  renderResizeHandles() {
    const {
      editMode,
      group: { blocks },
      textEditMode
    } = this.props
    if (!editMode || textEditMode || blocks.length > 1) return null

    const block = blocks[0]
    let handles
    if (block.aspect) handles = 'ne,se,nw,sw'
    else if (block.text) handles = 'e,w'
    else handles = 'n,s,e,w,ne,se,nw,sw'

    return handles.split(',').map(d => <ResizeHandle key={d} direction={d} />)
  }

  render() {
    const {
      group: { layout, blocks },
      editMode,
      textEditMode,
      placing,
      cropping,
      selected,
      layer,
      colWidth,
      dragOffset
    } = this.props

    let sortedBlocks = Object.values(blocks).sort(
      (a, b) => (a.stackingOrder || 0) - (b.stackingOrder || 0)
    )

    const showOptions = editMode && !placing && selected && !cropping

    return (
      <Manager>
        <Reference>
          {({ ref }) => (
            <DraggableCore
              disabled={!editMode || !!textEditMode || placing}
              onStart={(event, data) => this.dragStart(event, data)}
              onDrag={(event, data) => this.drag(event, data)}
              onStop={(event, data) => this.dragStop(event, data)}
              cancel=".details"
            >
              <Selectable
                selected={selected}
                dragging={this.state.action === 'move'}
                interactive={editMode && !placing && !cropping}
                layout={layout.scale(colWidth)}
                innerRef={ref}
              >
                {sortedBlocks.map(block => (
                  <BlockContainer
                    block={block}
                    key={block.id || 'placementBlock'}
                    groupLayout={layout.positive()}
                  />
                ))}

                {this.renderResizeHandles()}
              </Selectable>
            </DraggableCore>
          )}
        </Reference>
        {showOptions && !dragOffset && (
          <BlockOptions blocks={blocks} layer={layer} layout={layout} />
        )}
      </Manager>
    )
  }

  static contextTypes = {
    router: PropTypes.object
  }

  static propTypes = {
    board: PropTypes.object.isRequired,
    blocks: PropTypes.arrayOf(PropTypes.object.isRequired),
    editMode: PropTypes.bool.isRequired,
    textEditMode: PropTypes.bool.isRequired,
    setBlockLayout: PropTypes.func.isRequired,
    cloneBlocks: PropTypes.func.isRequired,
    select: PropTypes.func.isRequired,
    placing: PropTypes.bool.isRequired,
    selected: PropTypes.bool.isRequired,
    layer: PropTypes.string,
    group: PropTypes.object.isRequired,
    zoom: PropTypes.number.isRequired
    // snap: PropTypes.func.isRequired
  }
}

export default enhance(SelectableContainer)
